/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  createVodCaptionExport,
  downloadVodCaptionExport,
  listVodCaptionExports,
  listVodCaptionJobs,
  listVodCaptionSegments,
  normalizeVodCaptionGateState,
  updateVodCaptionSegment,
} from "../../../modules/captions/liveCaptionApi";
import "./EventMedia.css";

const DETAIL_SAMPLE = {
  media_idx: "sample-live-recording",
  video_title: "1일차 키노트 녹화본",
  register_source_label: "NCloud 녹화 가져오기 완료",
  file_version_label: "원본",
  media_type_label: "라이브 녹화본",
  version_kind_label: "원본",
  linked_session_count: 2,
  publish_state: "draft",
  duration_label: "01:12:34",
  caption_state_label: "자막 없음",
  caption_session_id: "1452",
  preview_state: "ready",
};

const DETAIL_INFO_ITEMS = [
  { label: "등록 방식", value: DETAIL_SAMPLE.register_source_label },
  { label: "파일 버전", value: DETAIL_SAMPLE.file_version_label },
  { label: "영상 구분", value: DETAIL_SAMPLE.media_type_label },
  { label: "원본·편집본", value: DETAIL_SAMPLE.version_kind_label },
  { label: "연결 세션", value: `${DETAIL_SAMPLE.linked_session_count}개` },
  { label: "공개 상태", value: DETAIL_SAMPLE.publish_state },
  { label: "재생 시간", value: DETAIL_SAMPLE.duration_label },
  { label: "자막 상태", value: DETAIL_SAMPLE.caption_state_label },
];

const LINKED_SESSIONS = [
  {
    session_idx: "1452",
    session_name: "오프닝 세션",
    linked_at_label: "2026-05-18 10:00",
    card_state: "세션 연결됨",
  },
  {
    session_idx: "1453",
    session_name: "패널 토론",
    linked_at_label: "2026-05-18 13:00",
    card_state: "N개 연결",
  },
];

const VERSION_ROWS = [
  {
    version_idx: "origin",
    version_label: "원본",
    file_label: "키노트 원본",
    status_label: "ready",
    created_at_label: "2026-05-18 10:30",
  },
  {
    version_idx: "edit-v1",
    version_label: "편집본 v1",
    file_label: "키노트 하이라이트",
    status_label: "draft",
    created_at_label: "2026-05-18 11:20",
  },
];

const TRANSCODE_PROGRESS_STEPS = [
  "validate_profile",
  "submit_to_engine",
  "poll_engine",
  "ingest_outputs",
  "finalize",
];

const TRANSCODE_OUTPUT_ROWS = [
  {
    output_id: "sample-master",
    kind: "master_playlist",
    resolution_label: "master",
    bitrate_label: "-",
    file_size_label: "manifest",
    authorization_scheme: "cf_signed_cookie",
  },
  {
    output_id: "sample-720p",
    kind: "variant_playlist",
    resolution_label: "720p",
    bitrate_label: "2.5 Mbps",
    file_size_label: "128 MB",
    authorization_scheme: "child_fetch_via_cf_signed_cookie",
  },
];

const MEDIA_DETAIL_TABS = {
  GENERAL: "general",
  CUT: "cut",
  CAPTIONS: "captions",
  EXPORT: "caption-download",
  TRANSCODE: "transcode",
};

const DETAIL_TABS = [
  { id: MEDIA_DETAIL_TABS.GENERAL, label: "미디어 일반" },
  { id: MEDIA_DETAIL_TABS.CUT, label: "영상 컷 편집" },
  { id: MEDIA_DETAIL_TABS.CAPTIONS, label: "자막 편집" },
  { id: MEDIA_DETAIL_TABS.EXPORT, label: "Export" },
  { id: MEDIA_DETAIL_TABS.TRANSCODE, label: "온디맨드 변환" },
];

const CUT_SPEC_SAMPLE = {
  trimStartMs: 0,
  trimEndMs: 180000,
  removeRanges: [{ startMs: 30000, endMs: 45000 }],
};

const CUT_JOB_STEPS = [
  { step: "validate_cut_spec", label: "cut_spec 검증", state: "done" },
  { step: "ffmpeg_render", label: "FFmpeg 렌더", state: "running" },
  { step: "register_version", label: "버전 등록", state: "pending" },
];

const CUT_FAILURE_STATE = {
  code: "ffmpeg_transient",
  label: "retry 가능",
};

const REPRESENTATIVE_SESSION_ROWS = [
  {
    session_media_link_id: "sml-1452",
    session_idx: "1452",
    session_name: "오프닝 세션",
    representative_version_id: "origin",
    caption_sync_required: true,
  },
];
const THUMBNAIL_POLICY_STATES = ["pending", "processing", "ready", "failed", "manual"];
const THUMBNAIL_STORAGE_CONTRACT = "object-storage-cdn-url-thumbnail-meta-no-db-blob";
const CAPTION_JOB = {
  status: "failed",
  failureReason: "의료용어 후처리 검수 대기",
  progressLabel: "2 / 5",
  sessionId: "caption-session-sample",
};
const CAPTION_JOB_STEPS = [
  "re_transcribe",
  "medical_correction",
  "speaker_mapping",
  "formatting",
  "export",
];
const CAPTION_MEDICAL_CONTEXT = {
  terms: "심혈관, 면역항암, 추적검사",
  term_snapshot_id: "term-snapshot-20260518",
  prompt_version: "vod-caption-medical-v1",
  resolver_version: "resolver-2026-05",
  brand_refs_digest: "brand-digest-sample",
};
const CAPTION_SEGMENT_WINDOW_SIZE = 25;
const CAPTION_DEMO_SEGMENT_TOTAL = 1200;
const CAPTION_DEMO_SEGMENTS = Array.from({ length: CAPTION_DEMO_SEGMENT_TOTAL }, (_, index) => {
  const sequence = index + 1;
  const sampleTexts = [
    "심혈관 위험도를 확인합니다.",
    "면역항암 치료 이력은 context를 참고합니다.",
    "추적검사 일정을 안내합니다.",
  ];
  return {
    segment_idx: `seg-${String(sequence).padStart(4, "0")}`,
    speaker_label: `Speaker ${(index % 3) + 1}`,
    timecode: `00:${String(Math.floor(sequence / 60)).padStart(2, "0")}:${String(sequence % 60).padStart(2, "0")}.000`,
    text: sampleTexts[index % sampleTexts.length],
  };
});
const CAPTION_EXPORT_FORMATS = ["VTT", "SRT", "TXT"];
const CAPTION_DEMO_EXPORT_GATE = {
  state: "demo",
  retentionAllowed: undefined,
  legalAllowed: undefined,
  costAllowed: undefined,
  failureReason: "demo_data_source",
};
const CAPTION_DEMO_EXPORT_HISTORY = [
  { export_idx: "export-vtt", format: "VTT", status: "ready", created_at_label: "2026-05-18 12:20" },
  { export_idx: "export-srt", format: "SRT", status: "blocked", created_at_label: "retention/legal gate" },
];

const getItems = (payload = {}) =>
  payload.items || payload.segments || payload.exports || payload.jobs || [];

const getSegmentId = (segment = {}) => segment.segment_idx || segment.id;

const normalizeCaptionSegment = (segment = {}, index = 0) => ({
  segment_idx: getSegmentId(segment) || `segment-${index}`,
  speaker_label: segment.speaker_label || segment.speakerLabel || segment.speaker || "",
  timecode: segment.timecode || segment.timecodeLabel || segment.startTimeLabel || "",
  text: segment.text || segment.captionText || "",
});

const normalizeCaptionExport = (item = {}) => ({
  export_idx: item.export_idx || item.id,
  format: (item.format || "").toUpperCase(),
  status: item.status || "unknown",
  created_at_label: item.created_at_label || item.createdAt || item.updatedAt || "",
});

const normalizeCaptionJob = (item = {}) => ({
  ...CAPTION_JOB,
  status: item.status || CAPTION_JOB.status,
  failureReason: item.errorMessage || item.errorCode || item.failureReason || CAPTION_JOB.failureReason,
  progressLabel: item.progressLabel || CAPTION_JOB.progressLabel,
  sessionId: item.sessionIdx || item.captionSessionId || CAPTION_JOB.sessionId,
});

const buildSegmentBaseline = (segments = []) =>
  segments.reduce((result, segment) => {
    result[getSegmentId(segment)] = {
      text: segment.text,
      speaker_label: segment.speaker_label,
      timecode: segment.timecode,
    };
    return result;
  }, {});

const buildChangedSegmentPatch = (segment = {}, original = {}) => {
  const patch = {};
  if (segment.text !== original.text) patch.text = segment.text;
  if (segment.speaker_label !== original.speaker_label) patch.speakerLabel = segment.speaker_label;
  if (segment.timecode !== original.timecode) patch.timecode = segment.timecode;
  return patch;
};

export default function EventMediaDetailGeneral(props) {
  const eventIdx = props?.match?.params?.eventIdx;
  const mediaIdx = props?.match?.params?.mediaIdx;
  const captionSessionId =
    props?.captionSessionId ||
    props?.captionSession?.id ||
    props?.media?.captionSessionId ||
    props?.media?.caption_session_id ||
    props?.match?.params?.sessionIdx ||
    DETAIL_SAMPLE.caption_session_id ||
    null;
  const [captionDataSource, setCaptionDataSource] = useState("demo");
  const [captionJob, setCaptionJob] = useState(CAPTION_JOB);
  const [captionSegments, setCaptionSegments] = useState(CAPTION_DEMO_SEGMENTS);
  const [captionSegmentBaseline, setCaptionSegmentBaseline] = useState(() =>
    buildSegmentBaseline(CAPTION_DEMO_SEGMENTS)
  );
  const [captionSegmentCursor, setCaptionSegmentCursor] = useState(null);
  const [captionSegmentNextCursor, setCaptionSegmentNextCursor] = useState(null);
  const [captionSegmentWindowStart, setCaptionSegmentWindowStart] = useState(0);
  const [captionSegmentTotalCount, setCaptionSegmentTotalCount] = useState(CAPTION_DEMO_SEGMENT_TOTAL);
  const [captionExportHistory, setCaptionExportHistory] = useState(CAPTION_DEMO_EXPORT_HISTORY);
  const [captionExportGate, setCaptionExportGate] = useState(CAPTION_DEMO_EXPORT_GATE);
  const [captionApiError, setCaptionApiError] = useState(null);
  const [exportFormat, setExportFormat] = useState("VTT");
  const [activeTab, setActiveTab] = useState(MEDIA_DETAIL_TABS.GENERAL);
  const isExportGateOpen = captionExportGate.state === "open";
  const isExportActionEnabled = Boolean(captionSessionId) && isExportGateOpen;
  const downloadableExport = captionExportHistory.find((item) => item.status === "ready");
  const hasNextCaptionWindow = Boolean(captionSegmentNextCursor) ||
    captionSegmentWindowStart + CAPTION_SEGMENT_WINDOW_SIZE < captionSegmentTotalCount;
  const VISIBLE_CAPTION_SEGMENTS = useMemo(
    () =>
      captionSegments.slice(
        captionSegmentWindowStart,
        captionSegmentWindowStart + CAPTION_SEGMENT_WINDOW_SIZE
      ),
    [captionSegmentWindowStart, captionSegments]
  );

  useEffect(() => {
    if (!captionSessionId) return undefined;

    let active = true;

    const setGateBlockedFromError = (error) => {
      if (!active) return;
      setCaptionExportGate(normalizeVodCaptionGateState(error, { state: "blocked", failureReason: error?.message }));
      setCaptionApiError(error?.message || "caption_api_error");
      setCaptionDataSource("demo");
    };

    const loadVodCaptionJobs = async () => {
      try {
        const result = await listVodCaptionJobs({
          captionSessionId,
          limit: 1,
        });
        if (!active) return;
        const [job] = getItems(result);
        if (job) setCaptionJob(normalizeCaptionJob(job));
        setCaptionExportGate(normalizeVodCaptionGateState(result, { state: "open" }));
        setCaptionDataSource("api");
      } catch (error) {
        setGateBlockedFromError(error);
      }
    };

    const loadVodCaptionSegments = async () => {
      try {
        const result = await listVodCaptionSegments({
          captionSessionId,
          limit: CAPTION_SEGMENT_WINDOW_SIZE,
          cursor: captionSegmentCursor,
        });
        if (!active) return;
        const nextSegments = getItems(result).map(normalizeCaptionSegment);
        setCaptionSegments(nextSegments);
        setCaptionSegmentBaseline(buildSegmentBaseline(nextSegments));
        setCaptionSegmentWindowStart(0);
        setCaptionSegmentNextCursor(result.nextCursor || null);
        setCaptionSegmentTotalCount(result.totalCount || result.total || nextSegments.length);
        setCaptionDataSource("api");
      } catch (error) {
        if (!active) return;
        setCaptionSegments(CAPTION_DEMO_SEGMENTS);
        setCaptionSegmentBaseline(buildSegmentBaseline(CAPTION_DEMO_SEGMENTS));
        setCaptionSegmentTotalCount(CAPTION_DEMO_SEGMENT_TOTAL);
        setCaptionApiError(error?.message || "caption_segments_api_error");
        setCaptionDataSource("demo");
      }
    };

    const loadVodCaptionExports = async () => {
      try {
        const result = await listVodCaptionExports({
          captionSessionId,
          limit: 20,
        });
        if (!active) return;
        setCaptionExportHistory(getItems(result).map(normalizeCaptionExport));
        setCaptionExportGate(normalizeVodCaptionGateState(result, { state: "open" }));
        setCaptionDataSource("api");
      } catch (error) {
        setGateBlockedFromError(error);
      }
    };

    loadVodCaptionJobs();
    loadVodCaptionSegments();
    loadVodCaptionExports();

    return () => {
      active = false;
    };
  }, [captionSegmentCursor, captionSessionId]);

  const handleSegmentFieldChange = (segmentId, field, value) => {
    setCaptionSegments((current) =>
      current.map((segment) =>
        segment.segment_idx === segmentId ? { ...segment, [field]: value } : segment
      )
    );
  };

  const handleSegmentSave = (segment) => {
    const segmentId = getSegmentId(segment);
    const changedPatch = buildChangedSegmentPatch(segment, captionSegmentBaseline[segmentId]);
    if (!Object.keys(changedPatch).length) return null;

    return updateVodCaptionSegment({
      segmentId,
      ...changedPatch,
      auditReason: "console_vod_caption_inline_edit",
    }).then((result) => {
      setCaptionSegmentBaseline((current) => ({
        ...current,
        [segmentId]: {
          text: segment.text,
          speaker_label: segment.speaker_label,
          timecode: segment.timecode,
        },
      }));
      return result;
    });
  };

  const handleCreateExport = () => {
    if (!isExportActionEnabled) return null;
    return createVodCaptionExport({
      captionSessionId,
      format: exportFormat.toLowerCase(),
    })
      .then((result) => {
        const nextExport = normalizeCaptionExport(result.export || result);
        setCaptionExportHistory((current) => [nextExport, ...current]);
        return result;
      })
      .catch((error) => {
        setCaptionExportGate(normalizeVodCaptionGateState(error, { state: "blocked", failureReason: error?.message }));
        setCaptionApiError(error?.message || "caption_export_create_error");
        return null;
      });
  };

  const handleDownloadExport = (exportId) => {
    if (!isExportActionEnabled || !exportId) return null;
    return downloadVodCaptionExport({ exportId })
      .then((result) => {
        if (result?.downloadUrl && typeof window !== "undefined") {
          window.location.assign(result.downloadUrl);
        }
        return result;
      })
      .catch((error) => {
        setCaptionExportGate(normalizeVodCaptionGateState(error, { state: "blocked", failureReason: error?.message }));
        setCaptionApiError(error?.message || "caption_export_download_error");
        return null;
      });
  };

  const handleCaptionWindowNext = () => {
    if (captionDataSource === "api" && captionSegmentNextCursor) {
      setCaptionSegmentCursor(captionSegmentNextCursor);
      return;
    }
    setCaptionSegmentWindowStart((current) =>
      Math.min(
        Math.max(captionSegmentTotalCount - CAPTION_SEGMENT_WINDOW_SIZE, 0),
        current + CAPTION_SEGMENT_WINDOW_SIZE
      )
    );
  };

  const handleCaptionWindowPrevious = () => {
    setCaptionSegmentWindowStart((current) => Math.max(0, current - CAPTION_SEGMENT_WINDOW_SIZE));
  };

  const handleKeyboardAction = (event, action) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  return (
    <DetailShell
      data-media-detail-general
      data-media-detail-path={`/v1/media/${mediaIdx}`}
      data-media-versions-path={`/v1/media/${mediaIdx}/versions`}
      data-media-sessions-path={`/v1/media/${mediaIdx}/sessions`}
    >
      <div className="media-detail-header">
        <div>
          <Link to={`/event/${eventIdx}/media`} className="media-detail-back">
            미디어 목록
          </Link>
          <h1>{DETAIL_SAMPLE.video_title}</h1>
        </div>
        <span className="media-detail-state">{DETAIL_SAMPLE.publish_state}</span>
      </div>

      <nav className="media-detail-tabs" data-media-detail-tabs aria-label="미디어 상세 탭">
        {DETAIL_TABS.map((tab) => (
          <span
            className={activeTab === tab.id ? "media-detail-tab is-active" : "media-detail-tab"}
            data-media-detail-tab
            data-active={activeTab === tab.id ? "true" : "false"}
            aria-selected={activeTab === tab.id}
            tabIndex={0}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => handleKeyboardAction(event, () => setActiveTab(tab.id))}
            key={tab.id}
          >
            {tab.label}
          </span>
        ))}
      </nav>

      <section className="media-detail-preview" data-media-preview>
        <div className="media-detail-preview-frame">
          <span>{DETAIL_SAMPLE.media_type_label}</span>
        </div>
        <div className="media-detail-preview-summary">
          <h2>미리보기</h2>
          <p>썸네일과 재생 정보는 실제 미디어 메타데이터 연결 후 표시됩니다.</p>
        </div>
      </section>

      <section className="media-detail-section" data-media-info-grid>
        <div className="media-detail-section-title">
          <h2>미디어 일반</h2>
        </div>
        <dl className="media-detail-info-grid">
          {DETAIL_INFO_ITEMS.map((item) => (
            <div className="media-detail-info-item" key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="media-detail-section" data-media-linked-sessions>
        <div className="media-detail-section-title">
          <h2>연결 세션</h2>
          <span>{LINKED_SESSIONS.length}개</span>
        </div>
        <div className="media-detail-table-wrap">
          <table>
            <thead>
              <tr>
                <th>세션명</th>
                <th>연결 시각</th>
                <th>카드 상태</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {LINKED_SESSIONS.map((session) => (
                <tr key={session.session_idx}>
                  <td className="media-detail-session-name">{session.session_name}</td>
                  <td>{session.linked_at_label}</td>
                  <td>{session.card_state}</td>
                  <td>
                    <span className="media-detail-action">연결 해제</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="media-detail-section" data-media-version-history>
        <div className="media-detail-section-title">
          <h2>버전 이력</h2>
        </div>
        <div className="media-detail-table-wrap">
          <table>
            <thead>
              <tr>
                <th>버전</th>
                <th>파일명</th>
                <th>상태</th>
                <th>생성 시각</th>
              </tr>
            </thead>
            <tbody>
              {VERSION_ROWS.map((version) => (
                <tr key={version.version_idx}>
                  <td className="media-detail-version-name">{version.version_label}</td>
                  <td>{version.file_label}</td>
                  <td>{version.status_label}</td>
                  <td>{version.created_at_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section
        className="media-detail-section"
        data-thumbnail-policy-ui
        data-thumbnail-storage-contract={THUMBNAIL_STORAGE_CONTRACT}
      >
        <div className="media-detail-section-title">
          <h2>썸네일</h2>
          <span>manual</span>
        </div>
        <div className="media-thumbnail-policy-grid">
          <div className="media-thumbnail-status-list" data-thumbnail-status-list>
            {THUMBNAIL_POLICY_STATES.map((state) => (
              <span data-thumbnail-policy-state={state} key={state}>
                {state}
              </span>
            ))}
          </div>
          <div className="media-thumbnail-control" data-thumbnail-manual-modal>
            <h2>수동 교체</h2>
            <p>이미지 선택 후 CDN 미리보기와 메타데이터를 확인합니다.</p>
            <span>object storage</span>
            <span>CDN URL</span>
            <span>thumbnail_meta</span>
          </div>
          <div className="media-thumbnail-control" data-thumbnail-reextract-confirm>
            <h2>자동 재추출 확인</h2>
            <p>manual 상태에서는 재추출 전 확인 단계가 필요합니다.</p>
          </div>
          <div className="media-thumbnail-control" data-thumbnail-fallback>
            <h2>Fallback</h2>
            <p>라이브 녹화본과 온디맨드 업로드 타입별 fallback을 분리합니다.</p>
          </div>
        </div>
      </section>

      <section
        className="media-detail-section"
        data-media-transcode-panel
        data-transcode-outputs-path={`/v1/captions/media/${mediaIdx}/versions/${VERSION_ROWS[0].version_idx}/transcode-outputs`}
        data-transcode-preview-url-path="/v1/captions/media/transcode-outputs/:outputId/preview-url"
      >
        <div className="media-detail-section-title">
          <h2>온디맨드 변환</h2>
          <span>Stage-1-6</span>
        </div>
        <div className="media-transcode-grid">
          <div className="media-transcode-card" data-transcode-profile-preview>
            <h2>profile preview</h2>
            <p>360p+480p+720p 파일럿 profile의 예상 output-minute와 quota 잔여를 먼저 확인합니다.</p>
            <span data-cost-gate-precheck-warning>
              cost gate 초과 예상 시 warning 표시
            </span>
            <span data-transcode-start-disabled aria-disabled="true">
              변환 시작 비활성
            </span>
          </div>
          <div className="media-transcode-card" data-transcode-progress-modal>
            <h2>Transcode progress modal</h2>
            <div className="media-transcode-step-list">
              {TRANSCODE_PROGRESS_STEPS.map((step, index) => (
                <span data-transcode-progress-step={step} key={step}>
                  {index + 1}. {step}
                </span>
              ))}
            </div>
            <span data-transcode-modal-restore-state>
              진행 중 상태 복귀
            </span>
            <span data-transcode-retry-action>
              retry
            </span>
            <span data-transcode-cancel-action>
              cancel
            </span>
          </div>
          <div className="media-transcode-card" data-transcode-preview-player-modal>
            <h2>Preview player modal</h2>
            <p>HLS 재생, 자막 토글, 화질 전환, poster 확인</p>
            <span data-transcode-preview-authorization-scheme="cf_signed_cookie">
              authorization_scheme: cf_signed_cookie
            </span>
            <span data-transcode-preview-url-action>
              preview-url API 입력은 master_playlist output_id만 허용
            </span>
            <span data-transcode-preview-audit>
              preview URL audit
            </span>
          </div>
        </div>
        <div className="media-detail-table-wrap" data-transcode-output-list>
          <table>
            <thead>
              <tr>
                <th>output</th>
                <th>kind</th>
                <th>해상도</th>
                <th>bitrate</th>
                <th>file size</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {TRANSCODE_OUTPUT_ROWS.map((output) => (
                <tr data-transcode-output-row key={output.output_id}>
                  <td className="media-detail-version-name">{output.output_id}</td>
                  <td>{output.kind}</td>
                  <td>{output.resolution_label}</td>
                  <td>{output.bitrate_label}</td>
                  <td>{output.file_size_label}</td>
                  <td>
                    <span className="media-detail-action">
                      {output.kind === "master_playlist" ? "preview 재생" : "child fetch"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="media-transcode-notice-grid">
          <div className="media-thumbnail-control" data-transcode-publish-routing-notice>
            <h2>publish 라우팅 안내</h2>
            <p>변환 완료 후 publish / 대표 영상 전환은 Stage-1-5 수동 액션으로만 진행됩니다.</p>
          </div>
          <div className="media-thumbnail-control" data-caption-sync-warning>
            <h2>caption_sync warning</h2>
            <p>자막 미동기 상태에서는 운영자 확인 후 preview 검수를 진행합니다.</p>
          </div>
          <div className="media-thumbnail-control" data-cost-gate-blocked-empty-error>
            <h2>EMPTY-LOADING-ERROR</h2>
            <p>cost_gate_blocked / legal_gate_blocked / retention_gate_blocked 상태를 분리 표시합니다.</p>
          </div>
        </div>
      </section>

      <section className="media-detail-section" data-media-followup-tabs>
        <div className="media-detail-placeholder-grid">
          <div
            className="media-detail-placeholder"
            data-media-cut-editor
            data-media-detail-tab-panel="cut"
            data-active={activeTab === MEDIA_DETAIL_TABS.CUT ? "true" : "false"}
            data-media-cut-job-create-path={`/v1/captions/media/${mediaIdx}/cut-jobs`}
            data-media-cut-job-status-path={`/v1/captions/media/cut-jobs/:jobId`}
            data-media-cut-job-retry-path={`/v1/captions/media/cut-jobs/:jobId/retry`}
            data-media-cut-job-cancel-path={`/v1/captions/media/cut-jobs/:jobId/cancel`}
          >
            <div className="media-detail-section-title">
              <h2>영상 컷 편집</h2>
              <span data-media-cut-create-draft-version>draft version 생성</span>
            </div>
            <p>후속 Stage에서 활성화됩니다.</p>
            <textarea
              aria-label="cut spec input"
              data-media-cut-spec-input
              readOnly
              value={JSON.stringify(CUT_SPEC_SAMPLE, null, 2)}
            />
            <div className="vod-caption-step-list" data-ffmpeg-progress-modal>
              {CUT_JOB_STEPS.map((item) => (
                <span
                  key={item.step}
                  data-cut-job-step={item.step}
                  data-cut-job-step-state={item.state}
                >
                  {item.label}
                </span>
              ))}
            </div>
            <div className="vod-caption-action-row" data-media-cut-failure-state={CUT_FAILURE_STATE.code}>
              <span>{CUT_FAILURE_STATE.label}</span>
              <span
                tabIndex={0}
                data-media-cut-retry
                onKeyDown={(event) => handleKeyboardAction(event, () => setActiveTab(MEDIA_DETAIL_TABS.CUT))}
              >
                retry
              </span>
            </div>
            <div
              className="media-thumbnail-control"
              data-representative-version-modal
              data-representative-version-path={`/v1/captions/media/${mediaIdx}/representative-version`}
              data-media-version-publish-path={`/v1/captions/media/${mediaIdx}/versions/:versionId/publish`}
              data-representative-scope="session-media-link"
            >
              <h2>대표 버전 전환</h2>
              <div data-representative-session-select>
                {REPRESENTATIVE_SESSION_ROWS.map((session) => (
                  <span
                    key={session.session_media_link_id}
                    data-session-media-link-id={session.session_media_link_id}
                    data-caption-sync-required={session.caption_sync_required ? "true" : "false"}
                  >
                    {session.session_name}
                  </span>
                ))}
              </div>
              <span data-representative-confirmation>EVENT_MGR 이상 확인 필요</span>
              <span data-publish-target-if-draft="true">publish_target_if_draft</span>
              <span data-force-with-unsynced-captions="true" data-force-requires-role="EVENT_MGR">
                force_with_unsynced_captions
              </span>
            </div>
          </div>
          <div className="media-detail-placeholder" data-vod-caption-editor>
            <div className="media-detail-section-title">
              <h2>자막 편집</h2>
              <span data-vod-caption-job-status>{captionJob.status}</span>
            </div>
            <div className="vod-caption-job-grid">
              <div data-vod-caption-step-progress>
                <strong>Step 진행도</strong>
                <p>{captionJob.progressLabel}</p>
                <div className="vod-caption-step-list">
                  {CAPTION_JOB_STEPS.map((step) => (
                    <span key={step}>{step}</span>
                  ))}
                </div>
              </div>
              <div data-vod-caption-failure-reason>
                <strong>실패 사유</strong>
                <p>{captionJob.failureReason}</p>
              </div>
              <div className="vod-caption-action-row">
                <span className="is-disabled" aria-disabled="true" data-vod-caption-retry-action>
                  retry 연동 대기
                </span>
                <span className="is-disabled" aria-disabled="true" data-vod-caption-reprocess-action>
                  reprocess 연동 대기
                </span>
                <span className="is-disabled" aria-disabled="true" data-vod-caption-auto-sync-action>
                  자동 싱크 연동 대기
                </span>
              </div>
            </div>
            <div
              className="vod-caption-medical-context"
              data-vod-caption-medical-context-readonly
              aria-readonly="true"
            >
              <strong>의료용어 context</strong>
              <p>{CAPTION_MEDICAL_CONTEXT.terms}</p>
              <dl className="vod-caption-context-meta">
                <div>
                  <dt>term_snapshot_id</dt>
                  <dd>{CAPTION_MEDICAL_CONTEXT.term_snapshot_id}</dd>
                </div>
                <div>
                  <dt>prompt_version</dt>
                  <dd>{CAPTION_MEDICAL_CONTEXT.prompt_version}</dd>
                </div>
                <div>
                  <dt>resolver_version</dt>
                  <dd>{CAPTION_MEDICAL_CONTEXT.resolver_version}</dd>
                </div>
                <div>
                  <dt>brand_refs_digest</dt>
                  <dd>{CAPTION_MEDICAL_CONTEXT.brand_refs_digest}</dd>
                </div>
              </dl>
            </div>
            <div
              className="vod-caption-segment-list"
              data-vod-caption-virtualized-segment-list
              data-segment-query="caption_segments_cursor"
              data-render-strategy="virtualized"
              data-visible-window-start={captionSegmentWindowStart}
              data-visible-segment-count={VISIBLE_CAPTION_SEGMENTS.length}
              data-total-segment-count={captionSegmentTotalCount}
              data-next-cursor={captionSegmentNextCursor || ""}
            >
              {VISIBLE_CAPTION_SEGMENTS.map((segment) => (
                <div className="vod-caption-segment-row" key={segment.segment_idx}>
                  <input
                    aria-label="자막 speaker edit"
                    data-vod-caption-segment-speaker-edit
                    value={segment.speaker_label}
                    onChange={(event) =>
                      handleSegmentFieldChange(segment.segment_idx, "speaker_label", event.target.value)
                    }
                  />
                  <input
                    aria-label="자막 time code edit"
                    data-vod-caption-segment-timecode-edit
                    value={segment.timecode}
                    onChange={(event) =>
                      handleSegmentFieldChange(segment.segment_idx, "timecode", event.target.value)
                    }
                  />
                  <input
                    aria-label="자막 segment inline edit"
                    data-vod-caption-segment-inline-edit
                    value={segment.text}
                    onChange={(event) =>
                      handleSegmentFieldChange(segment.segment_idx, "text", event.target.value)
                    }
                  />
                  <span
                    tabIndex={0}
                    aria-disabled={false}
                    data-vod-caption-segment-save-hook
                    onClick={() => handleSegmentSave(segment)}
                    onKeyDown={(event) => handleKeyboardAction(event, () => handleSegmentSave(segment))}
                  >
                    저장 hook
                  </span>
                </div>
              ))}
              <div className="vod-caption-action-row">
                <span
                  tabIndex={captionSegmentWindowStart > 0 ? 0 : -1}
                  aria-disabled={captionSegmentWindowStart <= 0}
                  className={captionSegmentWindowStart > 0 ? "" : "is-disabled"}
                  data-vod-caption-window-prev
                  onClick={handleCaptionWindowPrevious}
                  onKeyDown={(event) => handleKeyboardAction(event, handleCaptionWindowPrevious)}
                >
                  이전 window
                </span>
                <span
                  tabIndex={hasNextCaptionWindow ? 0 : -1}
                  aria-disabled={!hasNextCaptionWindow}
                  className={hasNextCaptionWindow ? "" : "is-disabled"}
                  data-vod-caption-window-next
                  onClick={handleCaptionWindowNext}
                  onKeyDown={(event) => handleKeyboardAction(event, handleCaptionWindowNext)}
                >
                  다음 window
                </span>
              </div>
            </div>
          </div>
          <div
            className="media-detail-placeholder"
            data-vod-caption-export
            data-vod-caption-data-source={captionDataSource}
            data-caption-api-error={captionApiError || ""}
            data-export-session-path={`/v1/captions/sessions/${captionSessionId || "demo"}/exports`}
            data-export-download-path="/v1/captions/exports/:exportId/download"
            data-export-gate-state={isExportGateOpen ? "open" : "blocked"}
          >
            <div className="media-detail-section-title">
              <h2>Export</h2>
              <span>VTT/SRT/TXT</span>
            </div>
            <p data-vod-caption-retention-gate>
              retention/legal gate 조건에서는 Export 생성과 다운로드가 비활성화됩니다.
              {captionExportGate.retentionAllowed === false ? " retention blocked" : ""}
            </p>
            <p data-vod-caption-legal-gate>
              법적 보존 기간 또는 동의 상태가 충족되지 않으면 안내만 표시합니다.
              {captionExportGate.legalAllowed === false ? " legal blocked" : ""}
            </p>
            <select
              aria-label="자막 Export format"
              value={exportFormat}
              onChange={(event) => setExportFormat(event.target.value)}
            >
              {CAPTION_EXPORT_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
            <div className="vod-caption-action-row">
              <span
                className={isExportActionEnabled ? "" : "is-disabled"}
                tabIndex={isExportActionEnabled ? 0 : -1}
                aria-disabled={!isExportActionEnabled}
                data-vod-caption-export-create
                onClick={handleCreateExport}
                onKeyDown={(event) => handleKeyboardAction(event, handleCreateExport)}
              >
                {isExportActionEnabled ? "Export 생성" : "Export 생성 disabled"}
              </span>
              <span
                className={isExportActionEnabled && downloadableExport ? "" : "is-disabled"}
                tabIndex={isExportActionEnabled && downloadableExport ? 0 : -1}
                aria-disabled={!(isExportActionEnabled && downloadableExport)}
                data-vod-caption-export-download
                onClick={() => handleDownloadExport(downloadableExport?.export_idx)}
                onKeyDown={(event) =>
                  handleKeyboardAction(event, () => handleDownloadExport(downloadableExport?.export_idx))
                }
              >
                {isExportActionEnabled && downloadableExport ? "다운로드" : "다운로드 disabled"}
              </span>
            </div>
            <div className="media-detail-table-wrap" data-vod-caption-export-history>
              <table>
                <thead>
                  <tr>
                    <th>포맷</th>
                    <th>상태</th>
                    <th>생성 시각</th>
                  </tr>
                </thead>
                <tbody>
                  {captionExportHistory.map((item) => (
                    <tr key={item.export_idx}>
                      <td>{item.format}</td>
                      <td>{item.status}</td>
                      <td>{item.created_at_label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </DetailShell>
  );
}

const DetailShell = styled.div`
  min-height: 100%;
  padding: 32px;
  color: var(--color-text-default);
  background: var(--color-surface-page);

  .media-detail-header,
  .media-detail-section-title {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
  }

  .media-detail-header {
    margin-bottom: 16px;
  }

  .media-detail-back {
    display: inline-flex;
    margin-bottom: 8px;
    font-size: 13px;
    color: var(--color-text-muted);
    text-decoration: none;
  }

  h1,
  h2 {
    margin: 0;
    color: var(--color-text-heading);
  }

  h1 {
    font-size: 20px;
    font-weight: 700;
  }

  h2 {
    font-size: 15px;
    font-weight: 700;
  }

  .media-detail-state,
  .media-detail-section-title span,
  .media-detail-action,
  .media-detail-preview-frame span {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: var(--radius);
    padding: 0 8px;
    font-size: 12px;
    background: var(--color-brand-soft-bg);
    color: var(--color-brand-soft-text);
  }

  .media-detail-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 18px;
    border-bottom: 1px solid var(--color-border-soft-alt);
  }

  .media-detail-tab {
    min-height: 32px;
    padding: 8px 12px;
    font-size: 13px;
    color: var(--color-text-muted);
  }

  .media-detail-tab.is-active {
    color: var(--color-text-heading);
    border-bottom: 2px solid var(--color-brand-soft-strong);
  }

  .media-detail-tab.is-disabled {
    opacity: 0.72;
  }

  .media-detail-preview,
  .media-detail-section {
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius);
    background: var(--color-surface-card);
  }

  .media-detail-preview {
    display: grid;
    grid-template-columns: minmax(260px, 420px) 1fr;
    gap: 18px;
    margin-bottom: 16px;
    padding: 16px;
  }

  .media-detail-preview-frame {
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: flex-end;
    padding: 12px;
    background: var(--color-surface-panel);
    border-radius: var(--radius);
  }

  .media-detail-preview-summary p,
  .media-detail-placeholder p,
  .vod-caption-job-grid p,
  .vod-caption-medical-context p {
    margin: 8px 0 0;
    font-size: 13px;
    color: var(--color-text-muted);
  }

  .media-detail-section {
    margin-top: 16px;
    padding: 16px;
  }

  .media-detail-info-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(140px, 1fr));
    gap: 10px;
    margin: 14px 0 0;
  }

  .media-detail-info-item {
    min-height: 72px;
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
    padding: 12px;
    background: var(--color-surface-muted);
  }

  dt,
  dd {
    margin: 0;
  }

  dt {
    font-size: 12px;
    color: var(--color-text-muted);
  }

  dd {
    margin-top: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-heading);
  }

  .media-detail-table-wrap {
    overflow-x: auto;
    margin-top: 14px;
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
  }

  table {
    width: 100%;
    min-width: 680px;
    border-collapse: collapse;
    font-size: 13px;
  }

  th,
  td {
    border-bottom: 1px solid var(--color-border-soft-alt);
    padding: 11px 12px;
    text-align: left;
  }

  th {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-muted);
    background: var(--color-surface-muted);
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .media-detail-session-name,
  .media-detail-version-name {
    font-size: 14px;
    font-weight: 700;
    color: var(--color-text-heading);
  }

  .media-detail-placeholder-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(160px, 1fr));
    gap: 12px;
  }

  .media-thumbnail-policy-grid {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) repeat(3, minmax(180px, 1fr));
    gap: 12px;
    margin-top: 14px;
  }

  .media-transcode-grid,
  .media-transcode-notice-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(220px, 1fr));
    gap: 12px;
    margin-top: 14px;
  }

  .media-transcode-notice-grid {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
  }

  .media-transcode-card {
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
    padding: 14px;
    background: var(--color-surface-muted);
  }

  .media-transcode-card p,
  .media-thumbnail-control p {
    margin: 8px 0 0;
    font-size: 13px;
    color: var(--color-text-muted);
  }

  .media-transcode-step-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .media-thumbnail-status-list,
  .media-thumbnail-control,
  .media-transcode-step-list span,
  .media-transcode-card span {
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
    padding: 14px;
    background: var(--color-surface-muted);
  }

  .media-thumbnail-status-list {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 8px;
  }

  .media-thumbnail-status-list span,
  .media-thumbnail-control span,
  .media-transcode-step-list span,
  .media-transcode-card span {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: var(--radius);
    padding: 0 8px;
    font-size: 12px;
    background: var(--color-brand-soft-bg);
    color: var(--color-brand-soft-text);
  }

  .media-thumbnail-control {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .media-detail-placeholder {
    border: 1px dashed var(--color-border-dashed-soft);
    border-radius: var(--radius);
    padding: 16px;
  }

  .vod-caption-job-grid {
    display: grid;
    grid-template-columns: minmax(160px, 1fr) minmax(160px, 1fr);
    gap: 12px;
    margin-top: 14px;
  }

  .vod-caption-job-grid > div,
  .vod-caption-medical-context,
  .vod-caption-segment-list {
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
    padding: 12px;
    background: var(--color-surface-muted);
  }

  .vod-caption-step-list,
  .vod-caption-action-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }

  .vod-caption-step-list span,
  .vod-caption-action-row span {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: var(--radius);
    padding: 0 8px;
    font-size: 12px;
    background: var(--color-brand-soft-bg);
    color: var(--color-brand-soft-text);
  }

  .vod-caption-action-row span.is-disabled {
    opacity: 0.58;
  }

  .vod-caption-medical-context,
  .vod-caption-segment-list {
    margin-top: 12px;
  }

  .vod-caption-context-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    gap: 10px;
    margin: 12px 0 0;
  }

  .vod-caption-segment-row {
    display: grid;
    grid-template-columns: minmax(100px, 130px) minmax(130px, 160px) minmax(180px, 1fr) auto;
    gap: 10px;
    align-items: center;
    min-height: 40px;
  }

  .vod-caption-segment-row + .vod-caption-segment-row {
    border-top: 1px solid var(--color-border-soft-alt);
    padding-top: 10px;
    margin-top: 10px;
  }

  .vod-caption-segment-row input {
    min-height: 32px;
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
    padding: 0 10px;
    color: var(--color-text-heading);
    background: var(--color-surface-card);
  }

  [data-vod-caption-export] select {
    min-height: 34px;
    margin-top: 12px;
    border: 1px solid var(--color-border-soft-alt);
    border-radius: var(--radius);
    padding: 0 10px;
    color: var(--color-text-heading);
    background: var(--color-surface-card);
  }

  @media (max-width: 767px) {
    padding: 20px;

    .media-detail-header,
    .media-detail-section-title {
      align-items: flex-start;
      flex-direction: column;
    }

    .media-detail-preview,
    .media-detail-info-grid,
    .media-detail-placeholder-grid,
    .media-thumbnail-policy-grid,
    .media-transcode-grid,
    .media-transcode-notice-grid,
    .vod-caption-job-grid,
    .vod-caption-segment-row {
      grid-template-columns: 1fr;
    }
  }
`;
